// src/components/songEdit/versions/VersionsList.tsx
import React, { useEffect, useState, useCallback } from 'react';
import apiClient from '../../../utils/apiClient';
import { AddVersionModal, VersionFormData } from './AddVersionModal';
import { VersionFiles } from './VersionFiles';
import { toast } from 'react-toastify';
import { useConfirmModal } from '../../common/ConfirmModal';
import './VersionsList.css';

export interface SongVersion {
  version_id: number;
  song_id: number;
  version_title: string | null;
  alt_title: string | null;
  arranger_name: string | null;
  arrangement_type: string | null;
  performance_type: string | null;
  key_signature: string | null;
  verses_included: string | null;
  video_link: string | null;
  canonical_filename: string | null;
  file_path: string | null;
  file_extension: string | null;
  version_number: number | null;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface VersionsListProps {
  songId: number;
}

export const VersionsList: React.FC<VersionsListProps> = ({ songId }) => {
  const { confirm, ConfirmModal } = useConfirmModal();
  const [versions, setVersions] = useState<SongVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<SongVersion | null>(null);
  // Track which versions have files collapsed (default: all expanded)
  const [collapsedVersionIds, setCollapsedVersionIds] = useState<Set<number>>(new Set());

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[VersionsList] Fetching versions for song:', songId);
      const response = await apiClient.get(`/songs/${songId}/versions`);
      console.log('[VersionsList] Versions fetched:', response.data);
      setVersions(response.data.data || []);
    } catch (err: any) {
      console.error('[VersionsList] Error fetching versions:', err);
      setError(err.response?.data?.message || 'Failed to load versions');
    } finally {
      setLoading(false);
    }
  }, [songId]);

  useEffect(() => {
    if (songId) {
      fetchVersions();
    }
  }, [songId, fetchVersions]);

  const formatKey = (key: string | null) => {
    if (!key) return '-';
    // Format key signature (e.g., KEY_D -> D, KEY_G_MAJOR -> G Major)
    return key.replace('KEY_', '').replace(/_/g, ' ');
  };

  const formatPerformanceType = (type: string | null) => {
    if (!type) return '-';
    // Common performance type abbreviations
    const types: Record<string, string> = {
      'CONG': 'Congregation',
      'CHOIR': 'Choir',
      'GOSPEL': 'Gospel Choir',
      'CANTOR': 'Cantor',
      'SOLOIST': 'Soloist'
    };
    return types[type] || type;
  };

  const handleAddVersion = () => {
    setSelectedVersion(null);
    setIsModalOpen(true);
  };

  const handleEditVersion = (version: SongVersion) => {
    setSelectedVersion(version);
    setIsModalOpen(true);
  };

  const handleDeleteVersion = (versionId: number) => {
    confirm({
      title: 'Delete Version',
      message: 'Are you sure you want to delete this version? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          console.log('[VersionsList] Deleting version:', versionId);
          await apiClient.delete(`/song-versions/${versionId}`);
          toast.success('Version deleted successfully');
          fetchVersions(); // Reload versions list
        } catch (err: any) {
          console.error('[VersionsList] Error deleting version:', err);
          toast.error(err.response?.data?.message || 'Failed to delete version');
        }
      }
    });
  };

  const handleSetDefault = async (versionId: number) => {
    try {
      console.log('[VersionsList] Setting default version:', versionId);
      await apiClient.put(`/song-versions/${versionId}`, { is_default: true });
      toast.success('Default version updated');
      fetchVersions(); // Reload to show updated default
    } catch (err: any) {
      console.error('[VersionsList] Error setting default:', err);
      toast.error(err.response?.data?.message || 'Failed to set default version');
    }
  };

  const toggleFilesCollapse = (versionId: number) => {
    setCollapsedVersionIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(versionId)) {
        newSet.delete(versionId);
      } else {
        newSet.add(versionId);
      }
      return newSet;
    });
  };

  const isFilesExpanded = (versionId: number) => !collapsedVersionIds.has(versionId);

  const handleSaveVersion = async (versionData: VersionFormData) => {
    try {
      if (selectedVersion) {
        // Update existing version
        console.log('[VersionsList] Updating version:', selectedVersion.version_id);
        await apiClient.put(`/song-versions/${selectedVersion.version_id}`, versionData);
        toast.success('Version updated successfully');
      } else {
        // Create new version
        console.log('[VersionsList] Creating version for song:', songId);
        await apiClient.post(`/songs/${songId}/versions`, versionData);
        toast.success('Version created successfully');
      }
      fetchVersions(); // Reload versions list
    } catch (err: any) {
      console.error('[VersionsList] Error saving version:', err);
      throw err; // Re-throw to let modal handle error display
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedVersion(null);
  };

  if (loading) {
    return (
      <div className="versions-list">
        <div className="versions-list__header">
          <h3>Song Versions</h3>
        </div>
        <div className="versions-list__loading">Loading versions...</div>
      </div>
    );
  }

  return (
    <div className="versions-list">
      <div className="versions-list__header">
        <h3>Song Versions</h3>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleAddVersion}
        >
          + Add Version
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {versions.length === 0 ? (
        <div className="versions-list__empty">
          <p>No versions found for this song.</p>
          <p className="text-muted">Click "Add Version" to create the first version.</p>
        </div>
      ) : (
        <div className="versions-list__table-container">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>Version Title</th>
                <th>Arranger</th>
                <th>Performance Type</th>
                <th>Key</th>
                <th>Arrangement Type</th>
                <th>Default</th>
                <th>Files</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((version) => (
                <React.Fragment key={version.version_id}>
                  <tr className={version.is_default ? 'table-primary' : ''}>
                    <td>
                      <strong>{version.version_title || version.alt_title || '-'}</strong>
                      {version.video_link && (
                        <a
                          href={version.video_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ms-2 text-primary"
                          title="Watch video"
                        >
                          🎥
                        </a>
                      )}
                    </td>
                    <td>{version.arranger_name || '-'}</td>
                    <td>{formatPerformanceType(version.performance_type)}</td>
                    <td>{formatKey(version.key_signature)}</td>
                    <td>{version.arrangement_type || '-'}</td>
                    <td>
                      {version.is_default ? (
                        <span className="badge bg-primary">Default</span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          onClick={() => handleSetDefault(version.version_id)}
                          title="Set as default version"
                        >
                          Set Default
                        </button>
                      )}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={() => toggleFilesCollapse(version.version_id)}
                        title={isFilesExpanded(version.version_id) ? 'Hide files' : 'Show files'}
                      >
                        {isFilesExpanded(version.version_id) ? '▼' : '▶'} Files
                      </button>
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() => handleEditVersion(version)}
                          title="Edit version"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => handleDeleteVersion(version.version_id)}
                          title="Delete version"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isFilesExpanded(version.version_id) && (
                    <tr className="version-files-row">
                      <td colSpan={8}>
                        <VersionFiles
                          versionId={version.version_id}
                          songId={songId}
                          versionTitle={version.version_title || version.alt_title || 'Version'}
                          isExpanded={true}
                          onToggle={() => toggleFilesCollapse(version.version_id)}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {versions.length > 0 && (
        <div className="versions-list__footer">
          <small className="text-muted">
            Total versions: {versions.length}
          </small>
        </div>
      )}

      <AddVersionModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveVersion}
        songId={songId}
        existingVersion={selectedVersion}
      />
      <ConfirmModal />
    </div>
  );
};
